import { fetchInsuranceCompaniesStatistics } from '../../api';
import { calculateInsuranceCompaniesChartOptions } from '@/features/order-statistics';
import { chartConfig } from '@/features/order-statistics/config';
import { Env } from '@/shared/config/environment';

const state = {
  insuranceCompaniesStatistics: null,
  isLoadingInsuranceCompanies: false,
  insuranceCompaniesError: null,
  activeFilters: {
    product: null,
    partner: null,
    period: [],
    periodMode: null,
  },

  insuranceCompaniesAmountChartOptions: null,
  insuranceCompaniesEarningsChartOptions: null,
  insuranceCompaniesCountChartOptions: null,
};

const mutations = {
  SET_INSURANCE_COMPANIES_STATISTICS(state, stats) {
    state.insuranceCompaniesStatistics = stats;
  },
  SET_LOADING_INSURANCE_COMPANIES(state, isLoading) {
    state.isLoadingInsuranceCompanies = isLoading;
  },
  SET_INSURANCE_COMPANIES_ERROR(state, error) {
    state.insuranceCompaniesError = error;
  },
  SET_INSURANCE_COMPANIES_AMOUNT_CHART_OPTIONS(state, options) {
    state.insuranceCompaniesAmountChartOptions = options;
  },
  SET_INSURANCE_COMPANIES_EARNINGS_CHART_OPTIONS(state, options) {
    state.insuranceCompaniesEarningsChartOptions = options;
  },
  SET_INSURANCE_COMPANIES_COUNT_CHART_OPTIONS(state, options) {
    state.insuranceCompaniesCountChartOptions = options;
  },
  SET_ACTIVE_FILTERS(state, filter) {
    state.activeFilters = filter;
  },
  SET_ACTIVE_FILTERS_PERIOD_MODE(state, mode) {
    state.activeFilters.periodMode = mode;
  },
};

const actions = {
  clearInsuranceCompaniesData({ commit }) {
    commit('SET_INSURANCE_COMPANIES_STATISTICS', null);
    commit('SET_INSURANCE_COMPANIES_AMOUNT_CHART_OPTIONS', null);
    commit('SET_INSURANCE_COMPANIES_COUNT_CHART_OPTIONS', null);
    commit('SET_INSURANCE_COMPANIES_EARNINGS_CHART_OPTIONS', null);
  },
  async fetchInsuranceCompaniesStatistics({ commit, dispatch, state }) {
    commit('SET_LOADING_INSURANCE_COMPANIES', true);
    commit('SET_INSURANCE_COMPANIES_ERROR', null);
    dispatch('clearInsuranceCompaniesData');

    try {
      const stats = await fetchInsuranceCompaniesStatistics(state.activeFilters);
      commit('SET_INSURANCE_COMPANIES_STATISTICS', stats);
      dispatch('calculateInsuranceCompaniesChartOptions');

      return stats;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося завантажити статистику страхових компаній';

      commit('SET_INSURANCE_COMPANIES_ERROR', errorMessage);
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося завантажити статистику страхових компаній',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_INSURANCE_COMPANIES', false);
    }
  },
  calculateInsuranceCompaniesChartOptions: ({ state, commit }) => {
    if (state.insuranceCompaniesStatistics) {
      const chartAmountOptions = calculateInsuranceCompaniesChartOptions(state.insuranceCompaniesStatistics, 'total_amount', 'currency');
      commit('SET_INSURANCE_COMPANIES_AMOUNT_CHART_OPTIONS', chartAmountOptions);

      const chartEarningsOptions = calculateInsuranceCompaniesChartOptions(state.insuranceCompaniesStatistics, 'total_earnings', 'currency');
      commit('SET_INSURANCE_COMPANIES_EARNINGS_CHART_OPTIONS', chartEarningsOptions);

      const chartCountOptions = calculateInsuranceCompaniesChartOptions(state.insuranceCompaniesStatistics, 'order_count', 'count');
      commit('SET_INSURANCE_COMPANIES_COUNT_CHART_OPTIONS', chartCountOptions);
    }
  },
  setupFilter: ({ state, commit, dispatch }, { filters, mode = 'replace', refetchData = true }) => {
    if (mode === 'replace') {
      commit('SET_ACTIVE_FILTERS', filters);
    } else {
      commit('SET_ACTIVE_FILTERS', { ...state.activeFilters, ...filters });
    }

    if (refetchData) {
      dispatch('fetchInsuranceCompaniesStatistics');
    }
  },
  setupPeriodMode: ({ commit }, mode) => {
    commit('SET_ACTIVE_FILTERS_PERIOD_MODE', mode);
  },
};

const getters = {
  insuranceCompaniesStatistics: (state) => state.insuranceCompaniesStatistics,
  isLoadingInsuranceCompanies: (state) => state.isLoadingInsuranceCompanies,
  insuranceCompaniesError: (state) => state.insuranceCompaniesError,

  isInsuranceCompaniesDataReady: (state) => { return state.isLoadingInsuranceCompanies === false && state.insuranceCompaniesStatistics !== null &&
    state.insuranceCompaniesAmountChartOptions !== null && state.insuranceCompaniesEarningsChartOptions !== null
    && state.insuranceCompaniesCountChartOptions !== null; },
  isChartEmpty: (state) => !state.insuranceCompaniesStatistics ||
    state.insuranceCompaniesStatistics.reduce((total, company) => total + (company?.statistics?.order_count || 0), 0) === 0,

  getStatisticChartOptions: (state) => (type) => { return state[`insuranceCompanies${type.charAt(0).toUpperCase() + type.slice(1)}ChartOptions`]; },
  getEmptyInsuranceCompaniesChartMessage: () => chartConfig.emptyMessages['message-2'].title,
  getActiveFilters: (state) => state.activeFilters,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
